/**
 * Detects the current tenant from the browser hostname.
 *
 *  - kk.mylms.in / kk.<any platform base domain>  → tenant by subdomain + base domain
 *  - learn.kkacademy.com                          → tenant by custom domain
 *  - mylms.in, *.up.railway.app, localhost, IPs   → platform host (no tenant)
 *
 * Detection accepts all of these; LINKS the platform generates are path-based
 * (https://mylms.in/tenant/kk) — see tenantPortalUrl at the foot of this file.
 *
 * The list of platform base domains is managed by the Super Admin (Administration →
 * Platform domains) and fetched from GET /api/public/platform at runtime (cached for an
 * hour in localStorage). BUILTIN_PLATFORM_HOSTS is the fallback when the API is unreachable.
 */
import axios from 'axios';
import { API_URL } from '../config/api';

export const RESERVED_SUBDOMAINS = new Set([
  'www', 'api', 'app', 'admin', 'mail', 'webmail',
  'cdn', 'static', 'assets', 'storage', 'docs',
  'preview', 'staging', 'dev', 'localhost', 'mylms',
]);

// Hosts that are always the platform itself (never a tenant), regardless of config.
export const BUILTIN_PLATFORM_HOSTS = [
  'up.railway.app',       // Railway-generated service domains (mylms.up.railway.app)
  'emergentagent.com',    // preview environments
  'preview.emergentagent.com',
  'localhost',
];

export const DEFAULT_BASE_DOMAINS = ['mylms.in'];

const CACHE_KEY = 'lms_platform_domains';
const CACHE_TTL_MS = 60 * 60 * 1000;

let platformPromise = null;

/** Base domains tenants are served under. Cached; safe to call often. */
export async function getPlatformDomains({ force = false } = {}) {
  if (!force) {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (cached && Array.isArray(cached.base_domains) && cached.base_domains.length && Date.now() - (cached.at || 0) < CACHE_TTL_MS) {
        return { base_domains: cached.base_domains, default_base_domain: cached.default_base_domain || cached.base_domains[0] };
      }
    } catch { /* ignore */ }
  }
  if (!platformPromise || force) {
    platformPromise = axios.get(`${API_URL}/public/platform`, { timeout: 8000 })
      .then((res) => {
        const d = res.data;
        if (!d || !Array.isArray(d.base_domains) || !d.base_domains.length) throw new Error('bad platform payload');
        const out = { base_domains: d.base_domains.map((x) => String(x).toLowerCase()), default_base_domain: String(d.default_base_domain || d.base_domains[0]).toLowerCase() };
        try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ...out, at: Date.now() })); } catch { /* ignore */ }
        return out;
      })
      .catch(() => ({ base_domains: DEFAULT_BASE_DOMAINS, default_base_domain: DEFAULT_BASE_DOMAINS[0] }))
      .finally(() => { platformPromise = null; });
  }
  return platformPromise;
}

/** Synchronous best-effort read of the cached base domains (for rendering hints). */
export function getCachedBaseDomains() {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    if (cached && Array.isArray(cached.base_domains) && cached.base_domains.length) return cached;
  } catch { /* ignore */ }
  return { base_domains: DEFAULT_BASE_DOMAINS, default_base_domain: DEFAULT_BASE_DOMAINS[0] };
}

/**
 * Parse hostname → { kind: 'subdomain' | 'custom_domain' | 'platform' | 'none', value, base }
 * `baseDomains` defaults to the built-in list; pass the runtime list for accurate results.
 */
export function classifyHost(hostname, baseDomains = DEFAULT_BASE_DOMAINS) {
  if (!hostname) return { kind: 'none', value: null };
  if (/^[\d.]+$/.test(hostname) || /^\[?[0-9a-fA-F:]+\]?$/.test(hostname.split(':')[0])) {
    return { kind: 'none', value: null };
  }
  const host = hostname.split(':')[0].toLowerCase();

  // Built-in platform hosts: the host itself or anything under it is the platform.
  for (const apex of BUILTIN_PLATFORM_HOSTS) {
    if (host === apex || host.endsWith('.' + apex)) return { kind: 'platform', value: host };
  }

  // Platform base domains: exact = platform root, one label below = tenant subdomain.
  const sorted = [...new Set([...(baseDomains || []), ...DEFAULT_BASE_DOMAINS])].sort((a, b) => b.length - a.length);
  for (const apex of sorted) {
    if (host === apex) return { kind: 'platform', value: host };
    if (host.endsWith('.' + apex)) {
      const prefix = host.slice(0, -('.' + apex).length);
      if (!prefix) return { kind: 'platform', value: host };
      const sub = prefix.split('.')[0];
      if (!sub || RESERVED_SUBDOMAINS.has(sub)) return { kind: 'platform', value: host };
      return { kind: 'subdomain', value: sub, base: apex };
    }
  }
  return { kind: 'custom_domain', value: host };
}

// Only accept a real tenant document. A mis-configured REACT_APP_BACKEND_URL makes the
// static server answer API calls with index.html (HTTP 200) — that must never be treated
// as a tenant.
function asTenant(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  if (typeof data.id !== 'string' || typeof data.subdomain !== 'string' || !data.subdomain) return null;
  return data;
}

export async function detectTenantFromHost(hostname) {
  const platform = await getPlatformDomains();
  const cls = classifyHost(hostname, platform.base_domains);
  if (cls.kind === 'none' || cls.kind === 'platform') return null;
  try {
    const host = hostname.split(':')[0].toLowerCase();
    const res = await axios.get(`${API_URL}/tenants/host/${encodeURIComponent(host)}`);
    return asTenant(res.data);
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ portal URLs
 *
 * Tenant portals are linked to by PATH — https://mylms.in/tenant/kk — not by subdomain.
 * A path needs no wildcard DNS and no certificate per tenant, so a portal works the
 * moment the tenant is created. Subdomain hosts (kk.mylms.in) still RESOLVE wherever
 * DNS happens to be set up — classifyHost / detectTenantFromHost are unchanged — the
 * platform just stops handing them out.
 *
 * An active custom domain still wins: it is configured per tenant and known to work.
 */

export const TENANT_PATH_PREFIX = '/tenant';

/** True when this tenant's own domain is set up and should be used ahead of the path. */
export function hasActiveCustomDomain(tenant) {
  return !!(tenant?.custom_domain && (tenant.custom_domain_meta?.status === 'active' || !tenant.custom_domain_meta));
}

/**
 * The origin tenant portals are served from. Prefers wherever the browser already is —
 * so links work on mylms.in, on *.up.railway.app and on localhost in dev alike — and
 * falls back to the configured default base domain when the current host is a tenant's.
 */
export function platformOrigin() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    if (classifyHost(window.location.hostname, getCachedBaseDomains().base_domains).kind === 'platform') {
      return window.location.origin;
    }
  }
  return `https://${getCachedBaseDomains().default_base_domain}`;
}

/** Path a tenant portal lives at on the platform host, e.g. /tenant/kk */
export function tenantPortalPath(tenant) {
  return tenant?.subdomain ? `${TENANT_PATH_PREFIX}/${tenant.subdomain}` : '';
}

/** Public URL of a tenant portal — https://mylms.in/tenant/kk, or its custom domain. */
export function tenantPortalUrl(tenant, { preferCustom = true } = {}) {
  if (!tenant?.subdomain && !tenant?.custom_domain) return '';
  if (preferCustom && hasActiveCustomDomain(tenant)) return `https://${tenant.custom_domain}`;
  return `${platformOrigin()}${tenantPortalPath(tenant)}`;
}

/** The portal URL without its scheme, for a table cell — mylms.in/tenant/kk */
export function tenantPortalLabel(tenant) {
  return tenantPortalUrl(tenant).replace(/^https?:\/\//, '');
}

/**
 * The subdomain host for a tenant — kk.mylms.in. Still reachable where wildcard DNS is
 * configured, so it is shown as an alternative, but it is never what the platform links to.
 */
export function tenantSubdomainHost(tenant, defaultBase) {
  if (!tenant?.subdomain) return '';
  const base = tenant.base_domain || defaultBase || getCachedBaseDomains().default_base_domain;
  return tenant.host || `${tenant.subdomain}.${base}`;
}
